import { useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import type React from 'react';

import MuxPlayer from '@mux/mux-player-react';
import type { MuxPlayerRefAttributes, MuxCSSProperties } from '@mux/mux-player-react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { parseSourceInfoFromUrl, type VideoSourceInfo } from '#src/app/shared/utils/petitionVideo';

import { VideoStateIndicatorSunrise } from './components/VideoStateIndicator/VideoStateIndicatorSunrise';
import { usePauseOnHidden } from './hooks/usePauseOnHidden';
import { useResponsiveVideo } from './hooks/useResponsiveVideo';

function isMuxPlayerSupported(): boolean {
	return typeof WeakRef !== 'undefined';
}

type VideoProps = React.ClassAttributes<HTMLVideoElement> & React.VideoHTMLAttributes<HTMLVideoElement>;
type AllowedVideoProps = Omit<
	VideoProps,
	'src' | 'ref' | 'style' | 'sx' | 'preload' | 'onLoadedMetadata' | 'height' | 'width'
>;

type MaxResolutionValue = React.ComponentProps<typeof MuxPlayer>['maxResolution'];

type Props = {
	/**
	 * The name of the user who created the video is displayed in the accessibility label.
	 */
	creator?: string;
	/**
	 * The id of the comment record where the video is stored.
	 */
	videoId: string;
	/**
	 * For now the videoUrl is passed directly to the `src` and must be a MP4 or WEBM, but we plan to
	 * try other options like cloudflare HLS/DASH streaming.
	 */
	videoUrl: string;
	/**
	 * Caps the highest rendition Mux delivers. Pass on small display surfaces (e.g. carousel thumbnails)
	 * so we don't pay for 1080p+ segments the user can't perceive. Only applies to Mux-backed videos.
	 */
	maxResolution?: MaxResolutionValue;
} & AllowedVideoProps;

function useVideoAriaLabel(creator?: string) {
	const { translate } = useI18n();
	return creator
		? translate('corgi.components.petition_video.actions.aria_label.video_from_user', { user_name: creator })
		: translate('corgi.components.petition_video.actions.aria_label.video_from_petition_supporter');
}

function getDataAndAriaProps(props: AllowedVideoProps): Record<string, unknown> {
	const result: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(props)) {
		if (key.startsWith('data-') || key.startsWith('aria-')) {
			result[key] = value;
		}
	}
	return result;
}

function MuxVideoPlayer({
	muxCallbackRef,
	sourceInfo,
	ariaLabel,
	videoId,
	sizeStyle,
	updateMediaDimensions,
	videoProps,
	maxResolution,
}: {
	muxCallbackRef: (node: MuxPlayerRefAttributes | null) => void;
	sourceInfo: Extract<VideoSourceInfo, { type: 'mux' }>;
	ariaLabel: string;
	videoId: string;
	sizeStyle: { aspectRatio?: string | number };
	updateMediaDimensions: () => void;
	videoProps: AllowedVideoProps;
	maxResolution?: MaxResolutionValue;
}) {
	const { tabIndex, ...restVideoProps } = videoProps;
	const spreadProps = getDataAndAriaProps(restVideoProps);

	return (
		<div
			className="h-full w-full"
			tabIndex={tabIndex}
			onClick={restVideoProps.onClick as unknown as React.MouseEventHandler<HTMLDivElement>}
			onKeyDown={restVideoProps.onKeyDown as unknown as React.KeyboardEventHandler<HTMLDivElement>}
			{...spreadProps}
		>
			<MuxPlayer
				ref={muxCallbackRef}
				playbackId={sourceInfo.muxPlaybackId}
				poster={sourceInfo.poster}
				aria-label={ariaLabel}
				data-testid={`supporter-video-player-${videoId}`}
				onLoadedData={updateMediaDimensions}
				preload={sourceInfo.preload}
				maxResolution={maxResolution}
				streamType="on-demand"
				className="rounded-large h-full max-h-full max-w-full overflow-hidden"
				style={{ aspectRatio: sizeStyle.aspectRatio, '--controls': 'none' } as MuxCSSProperties}
				muted={restVideoProps.muted}
				loop={restVideoProps.loop}
				autoPlay={restVideoProps.autoPlay}
				playsInline={restVideoProps.playsInline}
				onPlay={restVideoProps.onPlay as unknown as () => void}
				onPause={restVideoProps.onPause as unknown as () => void}
				onPlaying={restVideoProps.onPlaying as unknown as () => void}
				onError={restVideoProps.onError as unknown as () => void}
			/>
		</div>
	);
}

/** Wraps a video element with standardized sizing, poster, preloading, styling, and a11y. */
export const VideoPlayerSunrise = (({
	creator,
	videoId,
	videoUrl,
	maxResolution,
	ref: forwardedRef,
	...videoProps
}) => {
	const sourceInfo = useMemo(() => parseSourceInfoFromUrl(videoUrl), [videoUrl]);
	const isMux = sourceInfo.type === 'mux';
	const videoRef = useRef<HTMLVideoElement>(null);
	const muxPlayerRef = useRef<MuxPlayerRefAttributes>(null);
	const internalVideoRef = useRef<HTMLVideoElement>(null);

	// Eagerly extract the native <video> from MuxPlayer via a callback ref
	const muxCallbackRef = useCallback((node: MuxPlayerRefAttributes | null) => {
		(muxPlayerRef as React.MutableRefObject<MuxPlayerRefAttributes | null>).current = node;
		if (node) {
			const nativeVideo = node.media?.nativeEl;
			if (nativeVideo instanceof HTMLVideoElement) {
				(internalVideoRef as React.MutableRefObject<HTMLVideoElement | null>).current = nativeVideo;
			}
		}
	}, []);

	// Forward either the native video ref or the Mux internal video ref to the parent
	useImperativeHandle(
		forwardedRef,
		() => {
			if (isMux) {
				return internalVideoRef.current as HTMLVideoElement;
			}
			return videoRef.current as HTMLVideoElement;
		},
		// FIXME: workaround — ref is null due to ClientRender wrapper

		[videoRef.current, internalVideoRef.current, isMux],
	);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const {
		data: { sizeStyle },
		actions: { updateMediaDimensions },
	} = useResponsiveVideo({
		wrapperRef,
		videoElRef: isMux ? internalVideoRef : undefined,
	});
	usePauseOnHidden(isMux ? internalVideoRef : videoRef);
	const ariaLabel = useVideoAriaLabel(creator);
	return (
		<div ref={wrapperRef} className="rounded-large relative flex justify-center" style={sizeStyle}>
			<ClientRender>
				{() => {
					if (sourceInfo.type === 'mux') {
						if (!isMuxPlayerSupported()) return null;
						return (
							<MuxVideoPlayer
								muxCallbackRef={muxCallbackRef}
								sourceInfo={sourceInfo}
								ariaLabel={ariaLabel}
								videoId={videoId}
								sizeStyle={sizeStyle}
								updateMediaDimensions={updateMediaDimensions}
								videoProps={videoProps}
								maxResolution={maxResolution}
							/>
						);
					}
					return (
						<video
							aria-label={ariaLabel}
							data-testid={`supporter-video-player-${videoId}`}
							onLoadedMetadata={updateMediaDimensions}
							onLoadedData={updateMediaDimensions}
							poster={sourceInfo.type === 'cfs' ? sourceInfo.poster : undefined}
							preload={sourceInfo.preload}
							ref={videoRef}
							src={sourceInfo.src}
							className="rounded-large h-full max-h-full max-w-full"
							style={{ aspectRatio: sizeStyle.aspectRatio }}
							{...videoProps}
						/>
					);
				}}
			</ClientRender>
			{sourceInfo.type === 'cfs' || sourceInfo.type === 'mux' ? (
				<img
					aria-hidden
					data-testid={`supporter-video-player-${videoId}-poster`}
					onLoad={updateMediaDimensions}
					src={sourceInfo.poster}
					className="rounded-large pointer-events-none absolute top-0 -z-10 block h-full"
				/>
			) : null}
			<div className="pointer-events-none absolute top-0 flex h-full w-full place-content-center place-items-center">
				<VideoStateIndicatorSunrise videoRef={isMux ? internalVideoRef : videoRef} />
			</div>
		</div>
	);
}) as React.FC<
	Props & {
		ref?: React.RefObject<HTMLVideoElement | null>;
	}
>;
